/**
 * PRESS FEEDBACK ON TOUCH — the affordance the estate did not have.
 *
 * ⛔⛔ EVERY HOVER CUE WE SHIP IS SUPPOSED TO BE GATED BEHIND `@media (hover: hover)`, AND TAILWIND'S
 * PREFLIGHT SETS `-webkit-tap-highlight-color: transparent` ON EVERY APP. Both are individually
 * right — a hover lift that sticks after a tap is worse than none, and the OS grey flash does not
 * belong on a black player. Together they mean a phone gets NOTHING back when a button is pressed.
 * David, 2026-08-20: "on touch devices a lot of buttons do not give visual affordance on touchstart."
 *
 * ⛔⛔ THAT FIRST SENTENCE WAS AN ASSUMPTION AND IT WAS FALSE — 24 HOVER RULES WERE UNGATED, AND THE
 * DAY AFTER THIS FILE SHIPPED THE THREE ADMIN PORTALS STILL MISBEHAVED ON A TABLET. Installing a
 * press cue fixes the half where touch gets nothing; it does nothing about the half where an ungated
 * `:hover` STICKS to the last element tapped, which is the worse of the two because a stuck
 * highlight is indistinguishable from a selection. The premise is now measured rather than assumed
 * on every run — see `hoverGating.test.ts` beside this file, which walks the estate's stylesheets and
 * carries the rules still outstanding. ★ Do not restate the claim here; read it there.
 *
 * ★★ WHY THIS IS JAVASCRIPT AND NOT `:active`, WHICH IS THE OBVIOUS ANSWER. iOS Safari (and every
 * WKWebView, which is how James's iOS app hosts the viewer) applies `:active` on touch ONLY when the
 * element or one of its ancestors carries a touch handler — a long-standing, undocumented policy. So
 * a pure-CSS fix works on Android, does nothing on iPhone, and I cannot tell the two apart from a
 * headless Chromium here. `pointerdown` is specified, fires on touch-down on every engine, and can be
 * proved in a test. ⚠ The remedy usually recommended for the iOS quirk is an empty document-level
 * touch listener — i.e. a JavaScript listener anyway, but one whose effect is invisible in the code.
 *
 * ★ IT IS ONE DELEGATED LISTENER, NOT A PROP ON EVERY BUTTON. There are several hundred pressables
 * across seven surfaces; anything per-component would be applied to most of them and forgotten on the
 * rest, which is the state this fixes.
 *
 * ⛔ IT SETS AN ATTRIBUTE, NEVER A CLASS. React owns `className` and rewrites it wholesale on any
 * re-render, so a class added from outside can vanish mid-press — the same reason the TV app's keynav
 * engine writes its cursor through `classList` rather than letting React near it. React does not
 * manage attributes it never rendered, so `data-pressed` survives.
 *
 * The LOOK is not here: each surface mirrors its own hover treatment off `[data-pressed]` (a
 * brightness lift in the viewer, a cream outline on the player controls, a wash behind the timebar
 * cards). Mechanism shared, palette local.
 */

/** What counts as pressable — deliberately the same four the hover rules use. */
const PRESSABLE = 'button:not(:disabled), a[href], [role="button"], .clickable'

export const PRESSED_ATTR = 'data-pressed'

/**
 * ⚠ ONE AT A TIME, HELD IN A MODULE-LOCAL. Reading the DOM back for `[data-pressed]` on every release
 * would be a query per pointerup on every surface, and — worse — it cannot clear an element that has
 * since been unmounted mid-press, which is exactly what a button that navigates does.
 */
let pressed: Element | null = null

function release(): void {
  pressed?.removeAttribute(PRESSED_ATTR)
  pressed = null
}

/**
 * Start listening. Returns an uninstaller (tests use it; the apps never do — the listener lives as
 * long as the document).
 *
 * ⚠ CAPTURE PHASE, because a component may `stopPropagation()` on `pointerdown` to own a gesture —
 * the carousel and the timebar both do — and a swipe that begins on a card must still light it up.
 */
export function installPressFeedback(doc: Document = document): () => void {
  const down = (e: Event): void => {
    release() // a second finger, or a release we never saw
    const target = e.target
    if (!(target instanceof Element)) return
    const el = target.closest(PRESSABLE)
    /*
     * ⛔ `aria-disabled` IS NOT `:disabled`. A disabled-looking button implemented with the ARIA
     * attribute is still a `<button>` that matches the selector, and lighting it up promises an
     * action that will not happen.
     */
    if (!el || el.getAttribute('aria-disabled') === 'true') return
    el.setAttribute(PRESSED_ATTR, '')
    pressed = el
  }

  doc.addEventListener('pointerdown', down, true)
  /*
   * ⚠ `pointercancel` IS THE ONE THAT MATTERS ON A PHONE: it is what fires when the press turns out
   * to be the start of a scroll, and without it every flick down a rail would leave a lit card
   * behind it. `blur` covers the press that becomes a system gesture or an app switch.
   */
  for (const type of ['pointerup', 'pointercancel', 'dragstart', 'contextmenu'] as const) {
    doc.addEventListener(type, release, true)
  }
  doc.defaultView?.addEventListener('blur', release)

  return () => {
    doc.removeEventListener('pointerdown', down, true)
    for (const type of ['pointerup', 'pointercancel', 'dragstart', 'contextmenu'] as const) {
      doc.removeEventListener(type, release, true)
    }
    doc.defaultView?.removeEventListener('blur', release)
    release()
  }
}
